import { useState } from "react";

import { Icon, Intent, Tag } from "@blueprintjs/core";
import { Link } from "react-router";

import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { MessageButton } from "@/components/pieces/coaching/meetings/MessageButton";
import { ReviewHeaderButton } from "@/components/pieces/coaching/reviews/ReviewHeaderButton";
import { Avatar } from "@/components/pieces/users/Avatar";
import { getUser } from "@/components/session/JwtTokenManager";
import { RED, SUBTEXT_COLOR } from "@/css/constants";
import { ReviewsPageQuery } from "@/graphql";
import { FullReview } from "@/types/meetings";
import { CoachingUser, RoleView, StaffReviewsUser } from "@/types/users";
import { NonNullableArrayItem } from "@/utils/types";

import { CancelReviewDialog } from "./CancelReviewDialog";
import { CoachAcceptButtons } from "./CoachAcceptButtons";
import { ReviewIcon } from "./ReviewIcon";
import { getReviewAction, getReviewColor } from "./utils";

type ReviewsUser = NonNullable<ReviewsPageQuery["currentUser"]>;
type ReviewUserType = ReviewsUser | StaffReviewsUser;
type StaffReview = NonNullableArrayItem<ReviewUserType["reviews"]>;

type UserReview = NonNullableArrayItem<
  NonNullable<CoachingUser["ongoingReviews"]>
>;

interface ReviewBasicsProps {
  review: FullReview | UserReview | StaffReview;
  chatChannelId?: string | null;
  showMessageCount?: boolean;
  large?: boolean;
  showMessageButton?: boolean;
  view?: RoleView;
  customerId?: string;
}

export function ReviewBasics({
  review,
  chatChannelId,
  showMessageCount = false,
  large = false,
  showMessageButton = true,
  customerId,
}: ReviewBasicsProps) {
  const [showCancel, setShowCancel] = useState<boolean>(false);
  const currentUser = getUser();

  const canceledBox = (
    <Flex textAlign="right" alignItems="center" ml={[3, 0]}>
      <Box color={RED}>Review Canceled</Box>
    </Flex>
  );

  const isCoach = currentUser?.id === review.coach?.id;

  const coachButtons =
    !review.acceptedAt &&
    !review.completedAt &&
    !review.canceledAt &&
    isCoach ? (
      <CoachAcceptButtons id={review.id} />
    ) : null;

  const showMenuButton =
    !review.canceledAt &&
    !review.completedAt &&
    (!review.acceptedAt || isCoach);

  const redirectUrl = `/reviews`;

  const isMessageButtonVisible = showMessageButton && chatChannelId;

  return (
    <>
      <Flex
        alignItems={large ? "flex-start" : "center"}
        justifyContent="space-between"
        flexWrap="wrap"
      >
        <Flex
          alignItems={large ? "flex-start" : "center"}
          justifyContent="flex-start"
          style={
            review.canceledAt
              ? {
                  textDecoration: "line-through",
                  opacity: 0.4,
                }
              : undefined
          }
        >
          <Media greaterThan="xs">
            <Link to={`/reviews/${review.id}`}>
              <ReviewIcon size={50} backgroundColor={getReviewColor(review)} />
            </Link>
          </Media>
          <Box ml={[0, 24]}>
            <Box fontWeight={600} fontSize={[undefined, large ? 24 : 18]}>
              <Link to={`/reviews/${review.id}`}>
                <span>{review.product?.name}</span>
              </Link>
            </Box>
            <Box color={SUBTEXT_COLOR} mt={1}>
              {getReviewAction(review.state, isCoach, review.nextDate)}
            </Box>
            <Flex alignItems="center" mt={2}>
              {isCoach && review.customer ? (
                <>
                  <Avatar user={review.customer} size={22} />
                  <Box ml={2} color={SUBTEXT_COLOR}>
                    <Link to={`/client/${review.customer?.id}`}>
                      {review.customer?.name}
                    </Link>
                  </Box>
                  <Box ml={2}>
                    <Tag intent={Intent.PRIMARY} minimal={true} round>
                      Client
                    </Tag>
                  </Box>
                </>
              ) : (
                <>
                  {review.coach && (
                    <>
                      <Avatar user={review.coach} size={22} />
                      <Box ml={2} color={SUBTEXT_COLOR}>
                        {review.coach?.name}
                      </Box>
                    </>
                  )}
                </>
              )}
            </Flex>
          </Box>
        </Flex>
        <Media lessThan="md">
          <Flex mt={3} flexWrap="wrap">
            {!coachButtons && (showMessageButton || showMenuButton) ? (
              <>
                {isMessageButtonVisible ? (
                  <MessageButton
                    linkUrl={`/reviews/${review.id}`}
                    chatChannelId={chatChannelId}
                    showMessageCount={showMessageCount}
                  />
                ) : null}
                {showMenuButton ? (
                  <Box ml={isMessageButtonVisible ? 3 : undefined}>
                    <ReviewHeaderButton
                      isCoach={isCoach}
                      review={review}
                      setShowCancel={setShowCancel}
                      label={
                        <Box minWidth={20}>
                          <Icon icon="more" color={SUBTEXT_COLOR} size={20} />
                        </Box>
                      }
                    />
                  </Box>
                ) : null}
              </>
            ) : null}
          </Flex>
        </Media>
        <Media greaterThan="sm">
          <Flex mt={[3, 3, 0]} flexWrap="wrap" alignItems="center">
            {review.canceledAt ? canceledBox : null}
            {coachButtons}
            {!coachButtons && isMessageButtonVisible ? (
              <Box ml={3}>
                <MessageButton
                  linkUrl={`/reviews/${review.id}`}
                  chatChannelId={chatChannelId}
                  showMessageCount={showMessageCount}
                />
              </Box>
            ) : null}
            {!coachButtons && showMenuButton ? (
              <Box ml={3}>
                <ReviewHeaderButton
                  isCoach={isCoach}
                  review={review}
                  setShowCancel={setShowCancel}
                  label={
                    <Box minWidth={large ? 24 : 20}>
                      <Icon
                        icon="more"
                        color={SUBTEXT_COLOR}
                        size={large ? 24 : 20}
                      />
                    </Box>
                  }
                />
              </Box>
            ) : null}
          </Flex>
        </Media>
      </Flex>
      <Media lessThan="md">
        {coachButtons ? <Flex mt={3}>{coachButtons}</Flex> : null}
        {review.canceledAt ? <Box mt={3}>{canceledBox}</Box> : null}
      </Media>
      <CancelReviewDialog
        review={review}
        isCoach={isCoach}
        isOpen={showCancel}
        onCancel={() => setShowCancel(false)}
        onConfirm={() => (window.location.href = redirectUrl)}
        customerId={customerId}
      />
    </>
  );
}
